import { React, useEffect, useState } from "react";
import {
	Box,
	Chip,
	Typography,
	TableRow,
	TableCell,
	Tooltip,
	Button,
	Collapse,
	IconButton,
	FormControl,
	FormControlLabel,
	Switch,
} from "@mui/material";
import {
	DeleteOutlineOutlined,
	EditOutlined,
	Replay,
	SubdirectoryArrowRightRounded,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
	useToggleTransactionIncludeInAnalysisMutation,
	useDeleteTransactionMutation,
	useMarkTransactionAsConnectedWithAssetOrLiabilityMutation,
} from "../../services/queryHooks";
import EditTransactionModal from "./EditTransactionModal";
import MarkAsModal from "./MarkAsModal";
import { CategoryDisplay } from "./CategorySelect/CategoryDisplay";

export default function TransactionsTableRow({ row, wallets }) {
	const { t } = useTranslation();
	const theme = useTheme();
	const toggleIncludeInAnalysis =
		useToggleTransactionIncludeInAnalysisMutation();
	const deleteTransaction = useDeleteTransactionMutation();
	const markTransactionAsConnectedWithAssetOrLiability =
		useMarkTransactionAsConnectedWithAssetOrLiabilityMutation();
	const [open, setOpen] = useState(false);
	const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] =
		useState(false);
	const [isMarkAsModalOpen, setIsMarkAsModalOpen] = useState(false);

	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedSubcategory, setSelectedSubcategory] = useState("");

	useEffect(() => {
		setSelectedCategory(row.categoryId);
		setSelectedSubcategory(row.subcategoryId);
	}, [row]);

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, "0");
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	};

	const getWalletName = (walletId) => {
		const wallet = wallets.find((item) => item._id === walletId);
		return wallet ? wallet.name : t("unknown");
	};

	const getWalletCurrency = (walletId) => {
		const wallet = wallets.find((item) => item._id === walletId);
		return wallet ? wallet.currency : null;
	};

	const handleImportantClick = () => {
		toggleIncludeInAnalysis.mutate({
			transactionId: row._id,
			includeInAnalysis: !row.includeInAnalysis,
		});
	};

	const clearMarkedAs = () => {
		markTransactionAsConnectedWithAssetOrLiability.mutate({
			transactionId: row._id,
			investmentDebtPayoff: null,
		});
	};

	const handleDeleteClick = () => {
		deleteTransaction.mutate({
			transactionId: row._id,
		});
	};

	const isRelevantWalletCreated = () => {
		return (
			wallets.filter(
				(wallet) =>
					(wallet.type === "asset" || wallet.type === "liability") &&
					wallet.currency === row.currency
			).length > 0
		);
	};

	const isRowEditable = () => {
		return (
			row.type !== "wallet-balance-change" &&
			row.type !== "investment-debt-payoff-auto"
		);
	};

	const investmentLiabilityWallet = () => {
		if (row.investmentDebtPayoff) {
			const wallet = wallets.find(
				(item) => item._id === row.investmentDebtPayoff
			);
			return (
				(wallet.type === "asset"
					? `${t("investedIn")}`
					: `${t("debtPayedOff")}`) + wallet.name
			);
		} else {
			return null;
		}
	};
	const unimportantTooltipMessage = () => {
		if (!isRowEditable()) {
			return t("cannotEditAutoAddedTransaction");
		} else if (row.investmentDebtPayoff) {
			return t(
				"cannotMarkAsImportantTransactionInvestedInAssetOrLiability"
			);
		}
	};

	const tableCellStyles = {
		borderBottom: 0,
		opacity: row.includeInAnalysis ? 1 : 0.3,
	};

	return (
		<>
			<TableRow
				onClick={() => setOpen(!open)}
				key={row._id}
				sx={{
					cursor: "pointer",
					"&:hover": {
						backgroundColor: theme.palette.action.hover,
					},
				}}
			>
				<TableCell
					sx={{
						borderBottom: 0,
					}}
				>
					{row.type === "investment-debt-payoff-auto" ? (
						<Box
							sx={{
								ml: 1,
								display: "flex",
							}}
						>
							<SubdirectoryArrowRightRounded />
						</Box>
					) : (
						<Box
							sx={{
								opacity: row.includeInAnalysis ? 1 : 0.3,
							}}
						>
							{formatDate(row.createdAt)}
						</Box>
					)}
				</TableCell>
				<TableCell sx={tableCellStyles}>
					<Box>{row.description}</Box>
				</TableCell>
				<TableCell sx={{ pl: 0, borderBottom: 0 }}>
					<CategoryDisplay
						transaction={row}
						setSelectedCategory={setSelectedCategory}
						setSelectedSubcategory={setSelectedSubcategory}
						selectedCategory={selectedCategory}
						selectedSubcategory={selectedSubcategory}
						isOutlined={false}
					/>
				</TableCell>
				<TableCell sx={tableCellStyles}>
					<Box>{getWalletName(row.walletId)}</Box>
				</TableCell>
				<TableCell sx={tableCellStyles} align="right">
					<Typography
						sx={{
							fontWeight: "bold",
							display: "inline",
							color:
								row.amount > 0
									? theme.palette.values.positive
									: Number(row.amount) < 0
										? theme.palette.values.negative
										: "none",
							textWrap: "nowrap",
						}}
					>
						{row.amount.toFixed(2).toLocaleString()}{" "}
						{getWalletCurrency(row.walletId)}
					</Typography>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={5} sx={{ paddingBottom: 0, paddingTop: 0 }}>
					<Collapse in={open} unmountOnExit>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "space-between",
								pl: 2,
								borderBottom: 0,
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<Tooltip
									arrow
									placement="top"
									title={unimportantTooltipMessage()}
								>
									<FormControlLabel
										disabled={
											!isRowEditable() ||
											row.investmentDebtPayoff
										}
										control={
											<Switch
												checked={!row.includeInAnalysis}
												onChange={() =>
													handleImportantClick()
												}
											/>
										}
										label={t("unimportant")}
									/>
								</Tooltip>

								{!row.investmentDebtPayoff && (
									<Tooltip
										placement="top"
										arrow
										title={
											!isRelevantWalletCreated()
												? t(
														"noAssetsLiabilitiesForThisCurrency"
													)
												: !isRowEditable()
													? t(
															"cannotEditAutoAddedTransaction"
														)
													: ""
										}
									>
										<FormControl>
											<Button
												disabled={
													!isRelevantWalletCreated() ||
													!isRowEditable()
												}
												variant="outlined"
												onClick={() =>
													setIsMarkAsModalOpen(true)
												}
												sx={{ py: 0, px: 0.5 }}
											>
												{t("investmentDebtPayoff")}
											</Button>
										</FormControl>
									</Tooltip>
								)}
								{row.investmentDebtPayoff && (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
										}}
									>
										<Chip
											sx={{
												bgcolor:
													theme.palette.background
														.default,
											}}
											label={investmentLiabilityWallet()}
											onDelete={() => clearMarkedAs()}
											deleteIcon={
												<Tooltip
													title={t("revert")}
													arrow
													placement="right-end"
												>
													<Replay />
												</Tooltip>
											}
										/>
									</Box>
								)}
							</Box>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<Tooltip
									arrow
									placement="top"
									title={
										!isRowEditable()
											? t(
													"cannotEditAutoAddedTransaction"
												)
											: ""
									}
								>
									<span>
										<IconButton
											disabled={!isRowEditable()}
											sx={{ m: 1, mr: 0, px: 1 }}
											onClick={() =>
												setIsEditTransactionModalOpen(
													true
												)
											}
										>
											<EditOutlined />
										</IconButton>
									</span>
								</Tooltip>
								<IconButton
									sx={{ m: 1, px: 1 }}
									onClick={() => handleDeleteClick()}
								>
									<DeleteOutlineOutlined />
								</IconButton>
							</Box>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
			<EditTransactionModal
				isOpen={isEditTransactionModalOpen}
				onClose={() => setIsEditTransactionModalOpen(false)}
				wallets={wallets}
				transaction={row}
			/>
			<MarkAsModal
				isOpen={isMarkAsModalOpen}
				onClose={() => setIsMarkAsModalOpen(false)}
				wallets={wallets}
				transaction={row}
			/>
		</>
	);
}
